import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Suspense, lazy } from 'react';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { TooltipProvider } from './components/ui/tooltip';
import { AppShellWithSidebar } from './components/layout/AppShellWithSidebar';
import { InstallBanner, UpdateBanner } from './components/pwa';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const MunicipalityPage = lazy(() => import('./pages/MunicipalityPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const CyclesPage = lazy(() => import('./pages/CyclesPage'));
const CycleDetailPage = lazy(() => import('./pages/CycleDetailPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage'));
const InboundEmailsPage = lazy(() => import('./pages/InboundEmailsPage'));
const FieldInspectionPage = lazy(() => import('./pages/FieldInspectionPage'));
const InspectionPlanningPage = lazy(() => import('./pages/InspectionPlanningPage'));
const CamaImportPage = lazy(() => import('./pages/CamaImportPage'));
const MapDataPage = lazy(() => import('./pages/MapDataPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TemplateBuilderPage = lazy(() => import('./pages/TemplateBuilderPage'));
const RfiFormPage = lazy(() => import('./pages/RfiFormPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const AskAssistantPage = lazy(() => import('./pages/AskAssistantPage'));
const AssistantEscalationsAdminPage = lazy(() => import('./pages/AssistantEscalationsAdminPage'));
const AssistantKnowledgeAdminPage = lazy(() => import('./pages/AssistantKnowledgeAdminPage'));
const AssistantComparablesAdminPage = lazy(() => import('./pages/AssistantComparablesAdminPage'));
const AssistantSettingsAdminPage = lazy(() => import('./pages/AssistantSettingsAdminPage'));
const AssessorCalendarLinkPage = lazy(() => import('./pages/AssessorCalendarLinkPage'));
const AssessorCalendarCallbackPage = lazy(() =>
  import('./pages/AssessorCalendarLinkPage').then((m) => ({ default: m.AssessorCalendarCallbackPage })),
);
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/**
 * Protected route wrapper that requires authentication
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TooltipProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes - full screen (no sidebar) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/m/:municipalityId/cycles/:cycleId/field-inspection" element={<FieldInspectionPage />} />
              </Route>

              {/* Protected routes with sidebar layout */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AppShellWithSidebar />}>
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Municipality routes */}
                  <Route path="/m/:id" element={<MunicipalityPage />} />
                  <Route path="/m/:id/templates" element={<TemplatesPage />} />
                  <Route path="/templates/:templateId/edit" element={<TemplateEditorPage />} />
                  <Route path="/m/:id/templates/builder" element={<TemplateBuilderPage />} />
                  <Route path="/m/:id/templates/builder/:templateId" element={<TemplateBuilderPage />} />
                  <Route path="/m/:id/import" element={<ImportPage />} />
                  <Route path="/m/:id/cycles" element={<CyclesPage />} />
                  <Route path="/m/:id/cycles/:cycleId" element={<CycleDetailPage />} />
                  <Route path="/m/:id/cycles/:cycleId/inspection-planning" element={<InspectionPlanningPage />} />
                  <Route path="/m/:id/cycles/:cycleId/cama-import" element={<CamaImportPage />} />
                  <Route path="/m/:id/map-data" element={<MapDataPage />} />
                  <Route path="/m/:id/emails" element={<InboundEmailsPage />} />
                  <Route path="/m/:id/assistant-escalations" element={<AssistantEscalationsAdminPage />} />
                  <Route path="/m/:id/assistant-knowledge" element={<AssistantKnowledgeAdminPage />} />
                  <Route path="/m/:id/assistant-comparables" element={<AssistantComparablesAdminPage />} />
                  <Route path="/m/:id/assistant-settings" element={<AssistantSettingsAdminPage />} />
                  <Route path="/m/:id/assessor/calendar" element={<AssessorCalendarLinkPage />} />
                  <Route path="/m/:id/assessor/calendar/callback" element={<AssessorCalendarCallbackPage />} />
                  {/* TODO: Add more routes */}
                  {/* <Route path="/m/:id/p/:roll" element={<PropertyPage />} /> */}
                  {/* <Route path="/m/:id/export" element={<ExportPage />} /> */}

                  {/* Settings & Admin */}
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/users" element={<UsersPage />} />
                </Route>
              </Route>

              {/* Public pages - no auth required */}
              <Route path="/rfi/:token" element={<RfiFormPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/m/:muniId/ask" element={<AskAssistantPage />} />

              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Catch all - unknown routes render a real 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>

          <Toaster position="top-right" />
          <UpdateBanner />
          <InstallBanner />
        </TooltipProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
